import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { CalendarCheck, CalendarClock, Clock } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [
  { label: "+1 Month", days: 30 },
  { label: "+3 Months", days: 90 },
  { label: "+6 Months", days: 180 },
  { label: "+1 Year", days: 365 },
];
const PAYMENT_MODES = [
  { v: "NEFT_RTGS", label: "NEFT / RTGS" },
  { v: "UPI", label: "UPI" },
  { v: "CHEQUE", label: "Cheque" },
  { v: "CASH", label: "Cash" },
  { v: "OTHER", label: "Other" },
];
const GST_RATES = [0, 5, 12, 18, 28];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return "—"; }
};
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function daysRemainingChip(days) {
  if (days == null) return <Badge className="bg-slate-100 text-slate-500">—</Badge>;
  if (days > 30) return <Badge className="bg-emerald-100 text-emerald-800">{days}d left</Badge>;
  if (days >= 0) return <Badge className="bg-amber-100 text-amber-800">{days}d left</Badge>;
  return <Badge className="bg-rose-100 text-rose-800">Expired</Badge>;
}

export default function TenureModal({ tenant, onClose, onDone }) {
  const [extendDays, setExtendDays] = useState(365);
  const [useCustom, setUseCustom] = useState(false);
  const [customExpiry, setCustomExpiry] = useState("");
  const [amount, setAmount] = useState(0);
  const [gstRate, setGstRate] = useState(18);
  const [paymentMode, setPaymentMode] = useState("NEFT_RTGS");
  const [utrRef, setUtrRef] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const totalAmount = Math.round(Number(amount || 0) * (1 + Number(gstRate || 0) / 100) * 100) / 100;

  // Same "later of now-vs-current-expiry, plus extend_days" rule the
  // backend actually applies (routes_platform.renew_tenant) — this is a
  // preview only, the server recomputes it for real on submit.
  const previewExpiry = () => {
    if (useCustom && customExpiry) return new Date(`${customExpiry}T00:00:00Z`);
    const currentValidUntil = tenant.valid_until ? new Date(tenant.valid_until) : new Date();
    const base = currentValidUntil > new Date() ? currentValidUntil : new Date();
    return new Date(base.getTime() + extendDays * 86400000);
  };
  const preview = previewExpiry();
  const previewDaysDelta = tenant.valid_until
    ? Math.round((preview - new Date(tenant.valid_until)) / 86400000)
    : extendDays;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/tenants/${tenant.id}/renew`, {
        extend_days: Number(extendDays) || 0,
        custom_expiry: useCustom && customExpiry ? customExpiry : null,
        amount_inr: Number(amount) || 0,
        gst_rate: Number(gstRate) || 0,
        utr_ref: utrRef.trim(),
        payment_mode: paymentMode,
        notes: notes.trim(),
      });
      toast.success(`${tenant.name}'s tenure extended to ${fmtDate(data.valid_until)}`);
      onDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tenure-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {tenant.name}
            <Badge variant="secondary" className="text-[10px] capitalize">{tenant.plan?.tier || "starter"}</Badge>
            <Badge className={tenant.status === "suspended" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}
              data-testid="tenure-status-chip">
              {tenant.status === "suspended" ? "Suspended" : "Active"}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Current timeline */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">
              <CalendarCheck className="w-3.5 h-3.5" /> Package Activated On
            </div>
            <div className="text-sm font-semibold text-slate-800 mt-1">{fmtDate(tenant.valid_from)}</div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">
              <CalendarClock className="w-3.5 h-3.5" /> Service Expiry Date
            </div>
            <div className="text-sm font-semibold text-slate-800 mt-1" data-testid="tenure-current-expiry">{fmtDate(tenant.valid_until)}</div>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-medium">
              <Clock className="w-3.5 h-3.5" /> Days Remaining
            </div>
            <div className="mt-1.5">{daysRemainingChip(tenant.days_remaining)}</div>
          </div>
        </div>

        {/* Extension configuration */}
        <div className="space-y-2.5">
          <Label className="text-xs">Extend Tenure</Label>
          <div className="flex flex-wrap gap-2">
            {DURATIONS.map((d) => (
              <button key={d.days} type="button" data-testid={`tenure-duration-${d.days}`}
                onClick={() => { setUseCustom(false); setExtendDays(d.days); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  !useCustom && extendDays === d.days
                    ? "bg-brand text-white border-transparent"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}>
                {d.label} ({d.days}d)
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" id="tenure-use-custom" checked={useCustom} data-testid="tenure-use-custom"
              onChange={(e) => setUseCustom(e.target.checked)} className="h-4 w-4" />
            <Label htmlFor="tenure-use-custom" className="font-normal cursor-pointer text-sm">Use a custom expiry date instead</Label>
            <Input type="date" className="w-44 h-8 text-xs" data-testid="tenure-custom-date" disabled={!useCustom}
              value={customExpiry} onChange={(e) => setCustomExpiry(e.target.value)} />
          </div>
          <p className="text-xs text-slate-500" data-testid="tenure-preview">
            New Expiry Date will be: <b className="text-slate-800">{fmtDate(preview.toISOString())}</b>
            {" "}({previewDaysDelta >= 0 ? "+" : ""}{previewDaysDelta} days)
          </p>
        </div>

        {/* Financial & audit */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          <div>
            <Label className="text-xs">Base Amount (₹)</Label>
            <Input type="number" min={0} className="mt-1" data-testid="tenure-amount"
              value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">GST Rate</Label>
            <select className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="tenure-gst" value={gstRate} onChange={(e) => setGstRate(e.target.value)}>
              {GST_RATES.map((g) => <option key={g} value={g}>{g}%</option>)}
            </select>
          </div>
          <div className="sm:col-span-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-slate-500">Total Amount (incl. GST)</span>
            <span className="font-mono font-semibold text-slate-900" data-testid="tenure-total">{money(totalAmount)}</span>
          </div>
          <div>
            <Label className="text-xs">Payment Mode</Label>
            <select className="mt-1 w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="tenure-payment-mode" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
              {PAYMENT_MODES.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">UTR / Cheque Reference No.</Label>
            <Input className="mt-1" data-testid="tenure-utr" value={utrRef} onChange={(e) => setUtrRef(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Internal Notes</Label>
            <Textarea className="mt-1" rows={2} data-testid="tenure-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-brand" data-testid="tenure-submit" disabled={busy} onClick={submit}>
            {busy ? "Recording…" : "Record Payment & Extend Tenure"}
          </Button>
        </DialogFooter>

        {/* Audit history — tenure_history also carries SEAT_EXPANSION
            entries from the Manage Plan modal's seat add-on system
            (routes_platform.update_tenant_plan); this table is renewals
            only (its own columns don't apply to a seat change) — those
            show in PlatformTenants.jsx's Manage Plan dialog instead. */}
        {tenant.tenure_history?.filter((h) => !h.type || h.type === "RENEWAL").length > 0 && (
          <div className="pt-3 border-t border-slate-200">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Renewal History</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="tenure-history-table">
                <thead>
                  <tr className="text-left text-slate-400">
                    <th className="py-1.5 pr-3 font-medium">Date</th>
                    <th className="py-1.5 pr-3 font-medium">Added Days</th>
                    <th className="py-1.5 pr-3 font-medium">Total Paid</th>
                    <th className="py-1.5 pr-3 font-medium">UTR Ref</th>
                    <th className="py-1.5 pr-3 font-medium">Recorded By</th>
                  </tr>
                </thead>
                <tbody>
                  {tenant.tenure_history.filter((h) => !h.type || h.type === "RENEWAL").map((h) => (
                    <tr key={h.transaction_id} className="border-t border-slate-100">
                      <td className="py-1.5 pr-3 whitespace-nowrap text-slate-600">{fmtDate(h.created_at)}</td>
                      <td className="py-1.5 pr-3 font-mono">{h.extended_days}d</td>
                      <td className="py-1.5 pr-3 font-mono">{money(h.total_amount_inr)}</td>
                      <td className="py-1.5 pr-3 font-mono text-slate-500">{h.utr_ref || "—"}</td>
                      <td className="py-1.5 pr-3 text-slate-500">{h.recorded_by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
